import { useState, useEffect } from 'react';
import ReviewSection from '../../../components/reviews/ReviewSection';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';

interface Partner {
  id: number;
  name: string;
  business: string;
  description: string;
  detailDescription: string;
  icon: string;
  category: string;
  images: {
    main: string;
    gallery: string[];
  };
  contact: {
    phone?: string;
    address?: string;
  };
  specialties: string[];
  thumbnailImage?: string;
}

export default function PartnersGallery() {
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('전체');
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const initialPartners: Partner[] = [
    {
      id: 1,
      name: '박성숙',
      business: '남도음식전문가',
      description: '전통 남도음식의 맛과 정성을 담은 치유 음식 체험',
      detailDescription: '30년간 전통 남도음식을 연구하고 전수해온 박성숙 님이 직접 만드는 건강한 치유 음식입니다. 지역 특산물을 활용한 발효음식과 약선요리로 몸과 마음을 치유하는 특별한 경험을 제공합니다.',
      icon: 'ri-restaurant-line',
      category: '음식체험',
      images: {
        main: 'https://static.readdy.ai/image/d1a0c06a8edb29d2039b650faf22b9bc/2601c5470983240a35b87d6d3a0d4ef3.jpeg',
        gallery: [
          'https://public.readdy.ai/ai/img_res/c5259563fbaf3c0950e32d3d0752f82b.jpg',
          'https://readdy.ai/api/search-image?query=Korean%20medicinal%20cuisine%2C%20healthy%20herbal%20soup%2C%20traditional%20healing%20food%2C%20therapeutic%20ingredients%2C%20natural%20medicine%20food%2C%20warm%20healing%20broth&width=400&height=300&seq=food-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Traditional%20Korean%20cooking%20class%2C%20hands-on%20food%20experience%2C%20learning%20traditional%20recipes%2C%20cooking%20workshop%2C%20cultural%20food%20education&width=400&height=300&seq=food-3&orientation=landscape'
        ]
      },
      contact: {
        phone: '061-842-1234',
        address: '전라남도 고흥군'
      },
      specialties: ['전통발효음식', '약선요리', '지역특산물 요리', '치유음식 체험']
    },
    {
      id: 2,
      name: '복영문',
      business: '제이앤비농장 (유자농장)',
      description: '고흥 특산 유자를 활용한 다양한 치유 프로그램',
      detailDescription: '고흥의 청정 자연에서 자란 유자를 직접 재배하고 가공하여 다양한 유자 제품을 만들고 있습니다. 유자 수확 체험부터 유자차 만들기, 유자 가공품 제조까지 전 과정을 체험할 수 있습니다.',
      icon: 'ri-plant-line',
      category: '농장체험',
      images: {
        main: 'https://readdy.ai/api/search-image?query=Korean%20yuzu%20citrus%20farm%20with%20golden%20fruits%2C%20organic%20farming%2C%20therapeutic%20agriculture%2C%20peaceful%20orchard%20setting%2C%20healing%20citrus%20garden%2C%20traditional%20Korean%20farming%2C%20natural%20yuzu%20cultivation&width=600&height=400&seq=yuzu-farm-main&orientation=landscape',
        gallery: [
          'https://readdy.ai/api/search-image?query=Yuzu%20harvest%20experience%2C%20picking%20golden%20yuzu%20fruits%2C%20farm%20workers%20harvesting%20citrus%2C%20traditional%20Korean%20agriculture%2C%20organic%20fruit%20picking&width=400&height=300&seq=yuzu-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Yuzu%20tea%20making%20process%2C%20traditional%20Korean%20yuzu%20processing%2C%20making%20yuzu%20jam%20and%20tea%2C%20natural%20food%20preparation%2C%20healing%20citrus%20products&width=400&height=300&seq=yuzu-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Yuzu%20orchard%20in%20bloom%2C%20white%20yuzu%20flowers%2C%20spring%20citrus%20blossoms%2C%20peaceful%20farm%20landscape%2C%20therapeutic%20garden%20setting&width=400&height=300&seq=yuzu-3&orientation=landscape'
        ]
      },
      contact: {
        phone: '061-842-2345',
        address: '전라남도 고흥군'
      },
      specialties: ['유자재배', '유자가공', '농장체험', '유자제품 제조']
    },
    {
      id: 3,
      name: '정종권',
      business: '봄햇살케어팜 (숲체험)',
      description: '자연 속에서 몸과 마음을 치유하는 숲 치유 프로그램',
      detailDescription: '울창한 숲속에서 진행되는 전문적인 숲 치유 프로그램을 운영합니다. 숲 해설, 명상, 산림욕 등을 통해 현대인의 스트레스를 해소하고 자연과 하나되는 힐링 경험을 제공합니다.',
      icon: 'ri-tree-line',
      category: '숲치유',
      images: {
        main: 'https://static.readdy.ai/image/d1a0c06a8edb29d2039b650faf22b9bc/e1b5967c35fe028e68b5eb367eb7deee.jpeg',
        gallery: [
          'https://readdy.ai/api/search-image?query=Forest%20meditation%20session%2C%20people%20meditating%20in%20peaceful%20woods%2C%20nature%20therapy%2C%20mindfulness%20in%20forest%2C%20healing%20woodland%20experience&width=400&height=300&seq=forest-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Forest%20walking%20therapy%2C%20therapeutic%20forest%20paths%2C%20nature%20hiking%2C%20healing%20forest%20trails%2C%20peaceful%20woodland%20walks&width=400&height=300&seq=forest-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Forest%20education%20program%2C%20nature%20interpretation%2C%20learning%20about%20trees%20and%20plants%2C%20forest%20guide%20explaining%20nature%2C%20educational%20forest%20experience&width=400&height=300&seq=forest-3&orientation=landscape'
        ]
      },
      contact: {
        phone: '061-842-3456',
        address: '전라남도 고흥군'
      },
      specialties: ['숲치유프로그램', '산림욕', '자연명상', '스트레스 해소']
    },
    {
      id: 4,
      name: '최현귀',
      business: '두울리 (라벤더농장)',
      description: '라벤더의 향기로 마음의 평안을 찾는 아로마 치유',
      detailDescription: '보라빛 라벤더 밭에서 펼쳐지는 아로마테라피 체험을 제공합니다. 라벤더 수확부터 에센셜 오일 추출, 아로마 제품 만들기까지 향기로운 치유의 시간을 경험할 수 있습니다.',
      icon: 'ri-flower-line',
      category: '아로마테라피',
      images: {
        main: 'https://readdy.ai/api/search-image?query=Purple%20lavender%20field%20in%20Korea%2C%20therapeutic%20lavender%20farm%2C%20aromatic%20healing%20garden%2C%20peaceful%20lavender%20rows%2C%20natural%20aromatherapy%20setting%2C%20healing%20herbs%2C%20serene%20purple%20flowers&width=600&height=400&seq=lavender-farm-main&orientation=landscape',
        gallery: [
          'https://readdy.ai/api/search-image?query=Lavender%20harvest%20experience%2C%20picking%20purple%20lavender%20flowers%2C%20aromatherapy%20farming%2C%20natural%20herb%20collection%2C%20therapeutic%20flower%20picking&width=400&height=300&seq=lavender-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Essential%20oil%20extraction%20process%2C%20lavender%20oil%20making%2C%20natural%20aromatherapy%20production%2C%20distilling%20lavender%2C%20healing%20oil%20preparation&width=400&height=300&seq=lavender-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Aromatherapy%20workshop%2C%20making%20lavender%20products%2C%20natural%20soap%20and%20candle%20making%2C%20therapeutic%20craft%20experience%2C%20healing%20aromatherapy%20session&width=400&height=300&seq=lavender-3&orientation=landscape'
        ]
      },
      contact: {
        phone: '061-842-4567',
        address: '전라남도 고흥군'
      },
      specialties: ['라벤더재배', '아로마테라피', '에센셜오일', '향기치유']
    },
    {
      id: 5,
      name: '박지영',
      business: '세모실커피농장',
      description: '직접 재배한 커피로 만드는 특별한 커피 체험',
      detailDescription: '고흥의 청정 환경에서 직접 재배한 커피로 특별한 커피 체험을 제공합니다. 커피 재배부터 로스팅, 브루잉까지 전 과정을 체험하며 진정한 커피의 맛과 향을 느낄 수 있습니다.',
      icon: 'ri-cup-line',
      category: '커피체험',
      images: {
        main: 'https://readdy.ai/api/search-image?query=Korean%20coffee%20farm%2C%20organic%20coffee%20plants%2C%20coffee%20bean%20cultivation%2C%20therapeutic%20coffee%20experience%2C%20natural%20coffee%20plantation%2C%20healing%20coffee%20garden%2C%20peaceful%20agricultural%20setting&width=600&height=400&seq=coffee-farm-main&orientation=landscape',
        gallery: [
          'https://readdy.ai/api/search-image?query=Coffee%20bean%20harvest%2C%20picking%20ripe%20coffee%20cherries%2C%20coffee%20farming%20experience%2C%20organic%20coffee%20cultivation%2C%20traditional%20coffee%20agriculture&width=400&height=300&seq=coffee-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Coffee%20roasting%20process%2C%20traditional%20coffee%20roasting%2C%20artisan%20coffee%20preparation%2C%20coffee%20bean%20roasting%20workshop%2C%20therapeutic%20coffee%20making&width=400&height=300&seq=coffee-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Coffee%20cupping%20session%2C%20coffee%20tasting%20experience%2C%20learning%20coffee%20flavors%2C%20coffee%20education%20workshop%2C%20therapeutic%20coffee%20ceremony&width=400&height=300&seq=coffee-3&orientation=landscape'
        ]
      },
      contact: {
        phone: '061-842-5678',
        address: '전라남도 고흥군'
      },
      specialties: ['커피재배', '로스팅', '커피체험', '바리스타 교육']
    },
    {
      id: 6,
      name: '김은정',
      business: '한옥펜션',
      description: '전통 한옥에서 느끼는 고즈넉한 휴식과 치유',
      detailDescription: '전통 한옥의 아름다움과 현대적 편의시설을 갖춘 특별한 숙박 공간입니다. 한옥의 정취를 느끼며 자연과 함께하는 힐링 스테이를 경험할 수 있습니다.',
      icon: 'ri-home-line',
      category: '숙박시설',
      images: {
        main: 'https://readdy.ai/api/search-image?query=Traditional%20Korean%20hanok%20guesthouse%2C%20peaceful%20Korean%20architecture%2C%20healing%20accommodation%2C%20traditional%20Korean%20house%20with%20garden%2C%20serene%20hanok%20setting%2C%20therapeutic%20lodging%2C%20cultural%20healing%20space&width=600&height=400&seq=hanok-pension-main&orientation=landscape',
        gallery: [
          'https://readdy.ai/api/search-image?query=Traditional%20Korean%20hanok%20interior%2C%20peaceful%20bedroom%20with%20ondol%20heating%2C%20traditional%20Korean%20furniture%2C%20serene%20accommodation%2C%20cultural%20lodging%20experience&width=400&height=300&seq=hanok-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Korean%20hanok%20garden%2C%20traditional%20courtyard%2C%20peaceful%20outdoor%20space%2C%20healing%20garden%20setting%2C%20traditional%20Korean%20landscape&width=400&height=300&seq=hanok-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Traditional%20Korean%20tea%20ceremony%20in%20hanok%2C%20cultural%20experience%2C%20peaceful%20tea%20time%2C%20traditional%20Korean%20hospitality%2C%20healing%20cultural%20activity&width=400&height=300&seq=hanok-3&orientation=landscape'
        ]
      },
      contact: {
        phone: '061-842-6789',
        address: '전라남도 고흥군'
      },
      specialties: ['한옥숙박', '전통문화체험', '힐링스테이', '문화관광']
    },
    {
      id: 7,
      name: '손현숙',
      business: '하루애꽃 (치유체험농장)',
      description: '다양한 꽃과 식물을 통한 원예 치유 프로그램',
      detailDescription: '계절마다 피어나는 아름다운 꽃들과 함께하는 원예치료 프로그램을 운영합니다. 꽃과 식물을 기르고 가꾸는 과정을 통해 마음의 안정과 치유를 경험할 수 있습니다.',
      icon: 'ri-flower-fill',
      category: '원예치유',
      images: {
        main: 'https://readdy.ai/api/search-image?query=Korean%20healing%20flower%20farm%2C%20therapeutic%20garden%20with%20colorful%20flowers%2C%20horticultural%20therapy%20setting%2C%20peaceful%20flower%20fields%2C%20healing%20plants%20and%20flowers%2C%20natural%20therapy%20garden&width=600&height=400&seq=flower-farm-main&orientation=landscape',
        gallery: [
          'https://readdy.ai/api/search-image?query=Flower%20planting%20therapy%2C%20hands-on%20gardening%20experience%2C%20therapeutic%20horticulture%2C%20healing%20through%20gardening%2C%20peaceful%20flower%20cultivation&width=400&height=300&seq=flower-1&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Flower%20arrangement%20workshop%2C%20therapeutic%20flower%20arranging%2C%20healing%20through%20flowers%2C%20natural%20flower%20therapy%2C%20peaceful%20floral%20design&width=400&height=300&seq=flower-2&orientation=landscape',
          'https://readdy.ai/api/search-image?query=Seasonal%20flower%20garden%2C%20colorful%20therapeutic%20flowers%2C%20healing%20garden%20landscape%2C%20natural%20flower%20therapy%20setting%2C%20peaceful%20botanical%20garden&width=400&height=300&seq=flower-3&orientation=landscape'
        ]
      },
      contact: {
        phone: '061-842-7890',
        address: '전라남도 고흥군'
      },
      specialties: ['원예치료', '꽃재배', '정원가꾸기', '식물치유']
    }
  ];

  useEffect(() => {
    loadPartnersWithThumbnails();
  }, []);

  const loadPartnersWithThumbnails = async () => {
    try {
      const partnersWithThumbnails = await Promise.all(
        initialPartners.map(async (partner) => {
          // 각 업체의 갤러리에서 첫 번째 이미지 가져오기
          const { data: images, error } = await supabase
            .from('partner_images')
            .select('image_url')
            .eq('partner_id', partner.id.toString())
            .order('created_at', { ascending: true })
            .limit(1);

          if (error) {
            console.error('이미지 로드 오류:', error);
          }

          // 업로드된 이미지가 있으면 사용, 없으면 기본 이미지 사용
          // URL에서 타임스탬프 파라미터 제거
          let thumbnailUrl = partner.images.main;
          if (images && images.length > 0) {
            const imageUrl = images[0].image_url;
            // URL에서 쿼리 파라미터 제거
            thumbnailUrl = imageUrl.split('?')[0];
          }

          return {
            ...partner,
            thumbnailImage: thumbnailUrl
          };
        })
      );
      
      setPartners(partnersWithThumbnails);
    } catch (error) {
      console.error('업체 썸네일 로드 오류:', error);
      // 오류 발생 시 기본 이미지로 표시
      setPartners(initialPartners.map(p => ({ ...p, thumbnailImage: p.images.main })));
    } finally {
      setLoading(false);
    }
  };

  const categories = ['전체', '음식체험', '농장체험', '숲치유', '아로마테라피', '커피체험', '숙박시설', '원예치유'];

  const filteredPartners = selectedCategory === '전체' 
    ? partners 
    : partners.filter(partner => partner.category === selectedCategory);

  if (loading) {
    return (
      <div className="py-20 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-green-600 animate-spin mb-4"></i>
          <p className="text-gray-600">업체 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        {/* 헤더 */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-800 mb-6">참여업체 갤러리</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            고흥꿈트리 치유마을을 함께 만들어가는 7개 농가의 특별한 이야기와 체험을 만나보세요
          </p>
        </div>

        {/* 카테고리 필터 */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                selectedCategory === category
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-green-50 hover:text-green-600 shadow-md'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* 업체 갤러리 그리드 */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPartners.map((partner) => (
            <div 
              key={partner.id} 
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
              onClick={() => setSelectedPartner(partner)}
            >
              <div className="relative">
                <div className="w-full h-64">
                  <img
                    src={partner.thumbnailImage || partner.images.main}
                    alt={`${partner.business} - ${partner.name}`}
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (target.src !== partner.images.main) {
                        target.src = partner.images.main;
                      }
                    }}
                  />
                </div>
                <div className="absolute top-4 right-4 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                  <i className={`${partner.icon} text-green-600 text-xl`}></i>
                </div>
                <div className="absolute bottom-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  {partner.category}
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{partner.business}</h3>
                <p className="text-green-600 font-semibold mb-3">{partner.name} 님</p>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{partner.description}</p>
                
                <div className="flex flex-wrap gap-2">
                  {partner.specialties.slice(0, 2).map((specialty, index) => (
                    <span key={index} className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs">
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 상세 모달 */}
        {selectedPartner && (
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedPartner(null)}
          >
            <div 
              className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 모달 헤더 */}
              <div className="relative">
                <div className="w-full h-64">
                  <img
                    src={selectedPartner.thumbnailImage || selectedPartner.images.main}
                    alt={selectedPartner.business}
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (target.src !== selectedPartner.images.main) {
                        target.src = selectedPartner.images.main;
                      }
                    }}
                  />
                </div>
                <button
                  onClick={() => setSelectedPartner(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
                <div className="absolute bottom-4 left-4 bg-green-600 text-white px-4 py-2 rounded-full">
                  <span className="font-semibold">{selectedPartner.category}</span>
                </div>
              </div>

              {/* 모달 내용 */}
              <div className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <i className={`${selectedPartner.icon} text-green-600 text-2xl`}></i>
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-800">{selectedPartner.business}</h2>
                    <p className="text-green-600 font-semibold text-lg">{selectedPartner.name} 님</p>
                  </div>
                </div>

                <p className="text-gray-600 leading-relaxed mb-8 text-lg">
                  {selectedPartner.detailDescription}
                </p>

                {/* 전문분야 */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">전문분야</h3>
                  <div className="flex flex-wrap gap-3">
                    {selectedPartner.specialties.map((specialty, index) => (
                      <span key={index} className="bg-green-100 text-green-700 px-4 py-2 rounded-full font-semibold">
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>

                {/* 연락처 정보 */}
                <div className="bg-gray-50 rounded-xl p-6 mb-8">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">연락처 정보</h3>
                  <div className="space-y-3">
                    {selectedPartner.contact.phone && (
                      <div className="flex items-center gap-3">
                        <i className="ri-phone-line text-green-600 text-lg w-5 h-5 flex items-center justify-center"></i>
                        <span className="text-gray-700">{selectedPartner.contact.phone}</span>
                      </div>
                    )}
                    {selectedPartner.contact.address && (
                      <div className="flex items-center gap-3">
                        <i className="ri-map-pin-line text-green-600 text-lg w-5 h-5 flex items-center justify-center"></i>
                        <span className="text-gray-700">{selectedPartner.contact.address}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* 리뷰 섹션 */}
                <ReviewSection 
                  partnerId={selectedPartner.id.toString()}
                  partnerName={selectedPartner.business}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
